// #region Imports

import * as objectAssign from "object-assign";
import { INoteData } from "../../dto/INoteData";
import { IPosition } from "../../dto/IPosition";
import { INoteCategory } from "../../dto/INoteCategory";
import { ICourseData } from "../../dto/ICourseData";
import { sendEvent } from "../../modules/helpers/Analytics";

// #endregion Imports

// #region Interfaces

// Filter för "Relaterat material" (kort i högerpanelen/mobil-modalen).
// OCH-logik mellan de tre filtertyperna; tom = inaktiv.
export interface IRelatedFilter {
  types: string[];   // 'notes' | 'courses'
  colors: string[];  // 'color1'..'color5'
  query: string;
}

export interface IDocumentState {
  currentNote: INoteData;
  editorDisabled: boolean;
  hasSelection: boolean;
  notes: INoteData[];
  noteCategories: INoteCategory[];
  showNotes: boolean;
  showToolbar: boolean;
  showTooltip: boolean;
  tooltipPosition: IPosition;
  noteSearchFilter: string[];
  notesPanelExpaned: boolean;
  rollDownState: boolean;
  aNoteWasUpdated: number;
  forceEditNote: INoteData | null;
  updateNoteCollisionAndBundling: number;
  courses: ICourseData[];
  highlightedCourseIndex: number | null;
  relatedFilter: IRelatedFilter;
  filterPanelOpen: boolean;
  // Id på den anteckning vars note-editor faktiskt är öppen just nu (isInEditMode i
  // noteInlineEditor.tsx) – till skillnad från currentNote, som kan peka på en
  // anteckning som inte längre har sin editor öppen. Används för att en öppen editor
  // aldrig ska döljas av "Relaterat material"-filtret (se modules/helpers/relatedFilter.ts).
  editingNoteId: string | null;
}

// #endregion Interfaces

// #region Actions

const actions = {
  addMarking: "app/document/marking/add",
  addNote: "app/document/note/add",
  disableEditor: "app/document/note/disable",
  enableEditor: "app/document/note/enable",
  editNote: "app/document/note/edit",
  hideToolbar: "app/document/toolbar/hide",
  hideTooltip: "app/document/tooltip/hide",
  onDeselect: "app/document/marking/deselect",
  onSelect: "app/document/marking/select",
  refreshNote: "app/document/note/refresh",
  removeNote: "app/document/note/remove",
  showToolbar: "app/document/toolbar/show",
  toggleNoteVisibility: "app/document/notes/visible",
  setNoteCategories: "app/document/notes/categories/set",
  setNoteSearchFilter: "app/document/notes/searchfilter/set",
  setCurrentNote: "app/document/notes/current/set",
  setNotesPanelExpaned: "app/document/notes/toggle",
  rollDownExpanded: "app/document/notes/rolldowntoggle",
  aNoteWasUpdated: "app/document/notes/anotewasupdated",
  forceEditNote: "app/document/forceeditnote",
  updateNoteCollisionAndBundling: "app/document/updateNoteCollisionAndBundling",
  setCourses: "app/document/courses/set",
  setHighlightedCourseIndex: "app/document/courses/highlighted/set",
  setRelatedFilter: "app/document/relatedFilter/set",
  setFilterPanelOpen: "app/document/relatedFilter/panel/set",
  setEditingNoteId: "app/document/notes/editing/set",
};

// #endregion Actions

// #region State

const initialState: IDocumentState = {
  currentNote: null,
  editorDisabled: false,
  hasSelection: false,
  notes: (window as any).notes || [],
  noteCategories: [],
  showNotes: true,
  showToolbar: false,
  showTooltip: false,
  tooltipPosition: { x: 100, y: 100 },
  noteSearchFilter: [],
  notesPanelExpaned: false,
  rollDownState: false,
  aNoteWasUpdated: 0,
  updateNoteCollisionAndBundling: 0,
  forceEditNote: null,
  courses: (window as any).courses || [],
  highlightedCourseIndex: null,
  relatedFilter: { types: [], colors: [], query: "" },
  filterPanelOpen: false,
  editingNoteId: null,
};

// #endregion State

// #region Action creators

export function addMarking(note: INoteData) {
    sendEvent('addMarking', undefined, { pageTitle: note.pageHeading })
  return { type: actions.addMarking, note };
}

export function addNote(note: INoteData) {
  return { type: actions.addNote, note };
}

export function disableEditor() {
  return { type: actions.disableEditor };
}

export function enableEditor() {
  return { type: actions.enableEditor };
}

export function setNoteCategories(categories: INoteCategory[]) {
  return { type: actions.setNoteCategories, payload: categories };
}
export function setNoteSearchFilter(filterWords: string[]) {
  return { type: actions.setNoteSearchFilter, payload: filterWords };
}

export function editNote(note: INoteData) {
  return { type: actions.editNote, note };
}

export function forceEditNote(note: INoteData | null) {
  return { type: actions.forceEditNote, note };
}

export function updateNoteCollisionAndBundling(index: number) {
  return { type: actions.updateNoteCollisionAndBundling, index };
}

export function hideToolbar() {
  return { type: actions.hideToolbar };
}

export function hideTooltip() {
  return { type: actions.hideTooltip };
}

export function onDeselect() {
  return { type: actions.onDeselect };
}

export function onSelect(position: IPosition) {
  return { type: actions.onSelect, position };
}

export function refreshNote(note: INoteData) {
  return { type: actions.refreshNote, note };
}

export function removeNote(note: INoteData) {
  return { type: actions.removeNote, note };
}

export function showToolbar() {
  return { type: actions.showToolbar };
}

export function toggleNoteVisibility() {
  return { type: actions.toggleNoteVisibility };
}

export function setCurrentNote(note: INoteData) {
  return { type: actions.setCurrentNote, note };
}

export function setNotesPanelExpaned(visibility: boolean) {

  return { type: actions.setNotesPanelExpaned, visibility };
}

export function setRollDownExpanded(visibility: boolean) {
  return { type: actions.rollDownExpanded, visibility };
}

export function aNoteWasUpdated() {
  return { type: actions.aNoteWasUpdated };
}

export function setCourses(courses: ICourseData[]) {
  return { type: actions.setCourses, payload: courses };
}

export function setHighlightedCourseIndex(index: number | null) {
  return { type: actions.setHighlightedCourseIndex, payload: index };
}

export function setRelatedFilter(filter: IRelatedFilter) {
  return { type: actions.setRelatedFilter, payload: filter };
}

export function setFilterPanelOpen(open: boolean) {
  return { type: actions.setFilterPanelOpen, payload: open };
}

export function setEditingNoteId(noteId: string | null) {
  return { type: actions.setEditingNoteId, payload: noteId };
}

export const actionCreators = {
  addMarking,
  addNote,
  disableEditor,
  enableEditor,
  editNote,
  forceEditNote,
  updateNoteCollisionAndBundling,
  hideToolbar,
  hideTooltip,
  onDeselect,
  onSelect,
  refreshNote,
  removeNote,
  showToolbar,
  toggleNoteVisibility,
  setNoteCategories,
  setCurrentNote,
  aNoteWasUpdated,
  setNotesPanelExpaned,
  setRollDownExpanded,
  setCourses,
  setHighlightedCourseIndex,
  setRelatedFilter,
  setFilterPanelOpen,
  setEditingNoteId,
};

// #endregion Action creators

// #region Reducer

export default function reducer(state = initialState, action): IDocumentState {
  switch (action.type) {
    case actions.setNoteCategories:
      return {
        ...state,
        noteCategories: action.payload,
      };
    case actions.setNoteSearchFilter:
      return {
        ...state,
        noteSearchFilter: action.payload,
      };
    case actions.disableEditor:
      return {
        ...state,
        editorDisabled: true,
      };
    case actions.enableEditor:
      return {
        ...state,
        editorDisabled: false,
      };
    case actions.updateNoteCollisionAndBundling:
      return {
        ...state,
        updateNoteCollisionAndBundling: action,
      };
    case actions.forceEditNote:
      return {
        ...state,
        forceEditNote: action.note,
      };
    case actions.editNote:
      return {
        ...state,
        currentNote: action.note,
        hasSelection: false,
        showToolbar: true,
      };
    case actions.hideToolbar:
      return {
        ...state,
        //currentNote: null,
        showToolbar: false,
      };
    case actions.hideTooltip:
      return {
        ...state,
        showTooltip: false,
      };
    case actions.onDeselect:
      return {
        ...state,
        showTooltip: false,
        //hasSelection: false
      };
    case actions.onSelect:
      return {
        ...state,
        showTooltip: true,
        tooltipPosition: action.position,
        //hasSelection: true
      };
    case actions.refreshNote:
      return objectAssign({}, state, {
        //currentNote: null,
        hasSelection: false,
        editorDisabled: false,
        notes: state.notes.map((note) => {
          if (note.id === action.note.id) {
            return objectAssign({}, note, action.note);
          }
          return note;
        }),
      });
    case actions.removeNote:
      return objectAssign({}, state, {
        currentNote: null,
        editorDisabled: false,
        notes: state.notes.filter((note) => note.id !== action.note.id),
      });
    case actions.addNote:
      return objectAssign({}, state, {
        currentNote: action.note,
        hasSelection: false,
        editorDisabled: false,
        showToolbar: true,
        notes: [...state.notes, action.note],
      });
    case actions.addMarking:
      return objectAssign({}, state, {
        currentNote: null,
        hasSelection: false,
        editorDisabled: false,
        notes: [...state.notes, action.note],
      });
    case actions.showToolbar:
      return {
        ...state,
        showToolbar: true,
      };
    case actions.toggleNoteVisibility:
      return {
        ...state,
        showNotes: !state.showNotes,
      };
    case actions.setCurrentNote:
      return {
        ...state,
        currentNote: action.note,
      };
    case actions.aNoteWasUpdated:
      return {
        ...state,
        aNoteWasUpdated: state.aNoteWasUpdated + 1,
      };
    case actions.setCourses:
      return {
        ...state,
        courses: action.payload,
      };
    case actions.setHighlightedCourseIndex:
      return {
        ...state,
        highlightedCourseIndex: action.payload,
      };
    case actions.setRelatedFilter:
      return {
        ...state,
        relatedFilter: action.payload,
      };
    case actions.setFilterPanelOpen:
      return {
        ...state,
        filterPanelOpen: action.payload,
      };
    case actions.setEditingNoteId:
      return {
        ...state,
        editingNoteId: action.payload,
      };
    case actions.setNotesPanelExpaned:
      if (action.visibility !== state.notesPanelExpaned) {
        if (window.innerWidth < 1880) {
          action.visibility ? $(".content-navigation-wrapper").removeClass('pullBack') : $(".content-navigation-wrapper").removeClass("pushLeft semiHideMenu").addClass('pullBack');
          action.visibility ? $(".content__main-column").removeClass('pullBack') : $(".content__main-column").removeClass("pushLeft semiHideMenu").addClass('pullBack');
        }
        action.visibility ? $(".noteOverlay").show() : $(".noteOverlay").hide();
      }

      if (window.innerWidth < 1200) {
        return {
          ...state,
          notesPanelExpaned: action.visibility,
          rollDownState: action.visibility
        };
      } else {
        return {
          ...state,
          notesPanelExpaned: action.visibility,
        };
      }
      case actions.rollDownExpanded:
      return {
        ...state,
        rollDownState: action.visibility,
      };
    default:
      return state;
  }
}

// #endregion Reducer
